import { useState, useEffect } from 'react';
import { X, Save, Box, Ruler, TrendingUp, Maximize, Truck, Layers } from 'lucide-react';

export default function SiteSurveyModal({ isOpen, onClose, placeId, initialData, onSave }) {
  const [formData, setFormData] = useState({
    palletType: '',
    loadWeight: '',
    areaSize: '',
    isHighRacking: false,
    maxHeight: '',
    throughput: '',
    currentForklift: '',
    forkliftCount: '',
    applicationType: ''
  });

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...formData,
        ...initialData
      });
    }
  }, [initialData]);

  if (!isOpen) return null;

  const handleSave = () => {
    setIsSaving(true);
    fetch('/api/google_places/' + placeId, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: placeId,
        action: 'save_survey',
        survey_data: formData
      })
    })
    .then(res => res.json())
    .then(data => {
      if (data.status === 'success') {
        onSave(formData);
        onClose();
      } else {
        alert('เกิดข้อผิดพลาด: ' + data.message);
      }
    })
    .catch(err => {
      console.error(err);
      alert('เกิดข้อผิดพลาดในการบันทึกข้อมูล');
    })
    .finally(() => {
      setIsSaving(false);
    });
  };

  const SelectorButton = ({ active, onClick, children }) => (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        padding: '0.6rem 0.5rem',
        borderRadius: '6px',
        border: `2px solid ${active ? '#8b5cf6' : '#e5e7eb'}`,
        backgroundColor: active ? '#ede9fe' : 'white',
        color: active ? '#6d28d9' : '#4b5563',
        fontWeight: active ? 'bold' : 'normal',
        cursor: 'pointer',
        transition: 'all 0.2s',
        fontSize: '0.85rem'
      }}
    >
      {children}
    </button>
  );

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
      backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <div className="card" style={{ width: '500px', maxWidth: '95%', maxHeight: '90vh', overflowY: 'auto', position: 'relative', padding: '1.5rem' }}>
        <button 
          onClick={onClose}
          style={{ position: 'absolute', top: '1rem', right: '1rem', background: 'none', border: 'none', cursor: 'pointer' }}
        >
          <X size={24} color="#666" />
        </button>

        <h3 style={{ marginBottom: '0.5rem', color: '#6d28d9', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Box size={20} /> ทำ Site Survey
        </h3>
        <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '1.5rem' }}>
          บันทึกข้อมูลหน้างานเพื่อใช้ในการนำเสนอโซลูชันที่เหมาะสมที่สุด
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          
          {/* Pallet Type */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#374151' }}>
              ประเภทพาเลทที่ใช้งาน
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {['พาเลทเปิด', 'พาเลทปิด', 'พาเลทเฉพาะ'].map(type => (
                <SelectorButton 
                  key={type} 
                  active={formData.palletType === type} 
                  onClick={() => setFormData({...formData, palletType: type})}
                >
                  {type}
                </SelectorButton>
              ))}
            </div>
          </div>

          {/* Current Forklift & Count */}
          <div>
            <label style={{ marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#374151', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <Truck size={16} /> รถยกที่ปัจจุบันลูกค้าใช้งานอยู่ (ประเภทและจำนวน)
            </label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <select 
                value={formData.currentForklift}
                onChange={e => setFormData({...formData, currentForklift: e.target.value})}
                style={{ flex: 2, padding: '0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', outline: 'none', fontSize: '0.95rem', backgroundColor: 'white' }}
              >
                <option value="">-- เลือกประเภทรถยก --</option>
                <option value="power pallet">Power Pallet</option>
                <option value="order picker">Order Picker</option>
                <option value="reach truck">Reach Truck</option>
                <option value="counterbalance truck">Counterbalance Truck</option>
                <option value="อื่นๆ">อื่นๆ</option>
              </select>
              <input 
                type="number"
                value={formData.forkliftCount}
                onChange={e => setFormData({...formData, forkliftCount: e.target.value})}
                placeholder="จำนวนคัน"
                min="0"
                style={{ flex: 1, padding: '0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', outline: 'none', fontSize: '0.95rem' }}
              />
            </div>
          </div>

          {/* Application Type */}
          <div>
            <label style={{ marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#374151', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <Layers size={16} /> Application ที่ใช้งาน
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {['ขนส่งสินค้าแนวราบ', 'Stacking / Reaching', 'order picking'].map(app => (
                <SelectorButton 
                  key={app} 
                  active={formData.applicationType === app} 
                  onClick={() => setFormData({...formData, applicationType: app})}
                >
                  {app}
                </SelectorButton>
              ))}
            </div>
          </div>

          {/* Load Weight */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#374151' }}>
              น้ำหนักสินค้ายก (ต่อพาเลท)
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {['< 1 ตัน', '1 - 2 ตัน', '2 - 3 ตัน', '> 3 ตัน'].map(weight => (
                <SelectorButton 
                  key={weight} 
                  active={formData.loadWeight === weight} 
                  onClick={() => setFormData({...formData, loadWeight: weight})}
                >
                  {weight}
                </SelectorButton>
              ))}
            </div>
          </div>

          {/* Area Size */}
          <div>
            <label style={{ marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#374151', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <Maximize size={16} /> พื้นที่ใช้งานขนาดเท่าไหร่? (ตร.ม.)
            </label>
            <input 
              type="number"
              value={formData.areaSize}
              onChange={e => setFormData({...formData, areaSize: e.target.value})}
              placeholder="ระบุตัวเลขพื้นที่..."
              style={{ width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', outline: 'none', fontSize: '0.95rem' }}
            />
          </div>

          {/* High Racking */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#374151' }}>
              มีการจัดเก็บสินค้าในที่สูงหรือไม่? (High Racking)
            </label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <SelectorButton active={formData.isHighRacking === true} onClick={() => setFormData({...formData, isHighRacking: true})}>
                ✅ มี (Yes)
              </SelectorButton>
              <SelectorButton active={formData.isHighRacking === false} onClick={() => setFormData({...formData, isHighRacking: false, maxHeight: ''})}>
                ❌ ไม่มี (No)
              </SelectorButton>
            </div>
          </div>

          {/* Max Height (Conditional) */}
          {formData.isHighRacking && (
            <div style={{ padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px dashed #d1d5db' }}>
              <label style={{ marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#374151', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <Ruler size={16} /> ความสูงของชั้นวางระดับสูงสุด (เมตร)
              </label>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {['< 3m', '3 - 5m', '5 - 8m', '> 8m'].map(h => (
                  <SelectorButton 
                    key={h} 
                    active={formData.maxHeight === h} 
                    onClick={() => setFormData({...formData, maxHeight: h})}
                  >
                    {h}
                  </SelectorButton>
                ))}
              </div>
            </div>
          )}

          {/* Throughput */}
          <div>
            <label style={{ marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: '#374151', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <TrendingUp size={16} /> ปริมาณเข้า-ออก (Throughput)
            </label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {['น้อย', 'ปานกลาง', 'มาก (ตลอดเวลา)'].map(t => (
                <SelectorButton 
                  key={t} 
                  active={formData.throughput === t} 
                  onClick={() => setFormData({...formData, throughput: t})}
                >
                  {t}
                </SelectorButton>
              ))}
            </div>
          </div>

        </div>

        <div style={{ marginTop: '2rem', display: 'flex', gap: '1rem' }}>
          <button 
            onClick={onClose}
            style={{ flex: 1, padding: '0.75rem', borderRadius: '6px', backgroundColor: '#f3f4f6', color: '#4b5563', border: 'none', fontWeight: 600, cursor: 'pointer' }}
          >
            ยกเลิก
          </button>
          <button 
            onClick={handleSave}
            disabled={isSaving}
            style={{ flex: 2, padding: '0.75rem', borderRadius: '6px', backgroundColor: '#8b5cf6', color: 'white', border: 'none', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
          >
            <Save size={18} /> {isSaving ? 'กำลังบันทึก...' : 'บันทึกข้อมูล Survey'}
          </button>
        </div>

      </div>
    </div>
  );
}
